import { useState, useRef, useEffect } from 'react';

import styles from './FlatRoomFilter.module.css';
import { paths } from '../../../../../../../../settings';

type MultiSelectStaticFloorFilterProps = {
    onSelectRoom: (rooms: number[]) => void;
    reset: boolean;
};

const MultiSelectStaticFloorFilter: React.FC<MultiSelectStaticFloorFilterProps> = ({ onSelectRoom, reset }): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const staticOptions = [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' }
    ];

    useEffect(() => {
        if (reset && reset !== undefined) {
            setSelectedOptions([]);
        }
    }, [reset]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    /**
     * Toggles the open/closed state of the dropdown menu.
     * @returns {void}
    */
    const toggleDropdown = (): void => setIsOpen(!isOpen);

    /**
     * Handles selection and deselection of an option within the dropdown.
     * Updates the selected options based on the provided value and triggers `onSelectRoom` callback with the updated list.
     * @param {number} value - The option value to toggle in the selection.
     * @returns {void}
    */
    const handleSelectOption = (value: number): void => {
        setSelectedOptions((prevSelected) => {
            const updatedSelection = prevSelected.includes(value)
                ? prevSelected.filter((option) => option !== value)
                : [...prevSelected, value];

            setTimeout(() => onSelectRoom(updatedSelection), 0);
            return updatedSelection;
        });
    };

    /**
     * Renders a string representation of the selected options for display.
     * If no options are selected, it returns a "Wybierz" text.
     * @returns {string} - A string of selected options joined by commas, or 'Wybierz' if no options are selected.
    */
    const renderSelectedOptions = (): string => {
        if (selectedOptions.length === 0) return 'Wybierz';
        return selectedOptions.join(', ');
    };

    return (
        <div className={styles.floorFilter} ref={dropdownRef}>
            <span className={styles.label}>Pokoje</span>
            <div className={styles.dropdownHeader} onClick={toggleDropdown}>
                {renderSelectedOptions()}
                <img 
                    src={`${paths.assets}/icons/${isOpen ? 'arrow_up' : 'arrow_down'}.png`} 
                    alt={isOpen ? 'Zwiń' : 'Rozwiń'} 
                    width={24} 
                    height={24} 
                    className={styles.arrow}
                />
            </div>
            {isOpen && (
                <div className={styles.dropdownList}>
                    {staticOptions.map((option) => (
                        <div
                            key={option.value}
                            className={`${styles.dropdownItem} ${selectedOptions.includes(option.value) ? styles.selected : ''}`}
                            onClick={() => handleSelectOption(option.value)}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiSelectStaticFloorFilter;